/**
 * KENTEKEN-XCOUNTRY-1 — the same car across the border.
 *
 * A plate gives us make, model, fuel and year. `car_table` holds every foreign listing of
 * that car we have scraped, each with the import costs already computed by the pipeline
 * (BPM rest value, transport, fixed costs, VAT exposure). So "what does this car cost in
 * Germany, and what does one land for in the Netherlands" is a grouping, not a model.
 *
 * Landed = asking + import_fixed_costs + bpm_rest + potential_vat_liability, the pipeline's own
 * total_cost (profit.py) plus the VAT a private buyer cannot reclaim on a BTW-auto. These are
 * ASKING prices, not transaction prices; the caveat is printed next to the table.
 */

// market_variants speaks English fuel names, car_table speaks Dutch.
export const FUEL_TO_CAR_TABLE = {
  petrol: 'benzine',
  diesel: 'diesel',
  hybrid: 'hybride',
  electric: 'elektrisch',
  lpg: 'lpg',
}

export const YEAR_WINDOW = 2
export const MILEAGE_WINDOW_MIN = 20_000
export const MILEAGE_WINDOW_PCT = 0.25
export const MAX_ROWS = 1500
export const MIN_PER_COUNTRY = 3

const num = v => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function median(values) {
  const xs = values.filter(v => v != null && Number.isFinite(v)).sort((a, b) => a - b)
  if (xs.length === 0) return null
  const mid = Math.floor(xs.length / 2)
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2
}

export function landedCost(row) {
  const price = num(row.price_amount_eur)
  if (price == null || price <= 0) return null
  const costs = [row.import_fixed_costs, row.bpm_rest, row.potential_vat_liability].map(num)
  // Missing cost evidence must not become a zero-cost import.
  if (costs.some(v => v === null || v < 0)) return null
  return price + costs.reduce((a, b) => a + b, 0)
}

export function powerBandBounds(band) {
  const bounds = { '<100': [0, 100], '100-129': [100, 130], '130-159': [130, 160],
    '160-199': [160, 200], '200-249': [200, 250], '250-319': [250, 320], '320+': [320, null] }
  return bounds[band] || null
}

/** The PocketBase filter for one plate's comparables abroad (and at home). */
export function mileageWindow(km) {
  const value = num(km)
  if (value == null || value < 0) return null
  const radius = Math.max(MILEAGE_WINDOW_MIN, value * MILEAGE_WINDOW_PCT)
  return [Math.max(0, Math.round(value - radius)), Math.round(value + radius)]
}

/** The served contract's freshness window for the discovery surface. */
const FRESH_WINDOW_DAYS = 7

export function crossCountryFilter({ make, model, fuel, year, mileageKm, hpBand }) {
  const esc = v => String(v).replace(/'/g, "\\'")
  // RELEASE-EXPIRE-1 (2026-09-05): this table is the only place a foreign asking price is
  // shown as fact, and it applied NEITHER of the served contract's two currency clauses.
  // `release_active` was set at upload and never reset, so for any brand that stopped being
  // re-released this presented August listings as today's cross-border market: on 2026-09-05
  // that was 2,125,953 rows, 91% of the table. The list view was protected by its own
  // freshness filter; this was not. Both clauses now match the contract's discovery surface.
  const parts = [
    `make='${esc(make)}'`, `model='${esc(model)}'`, 'price_amount_eur>0',
    "source_flag!='gone'", 'release_active=true',
    `scraped_at>='${new Date(Date.now() - FRESH_WINDOW_DAYS * 864e5).toISOString().slice(0, 10)}'`,
  ]
  const carFuel = FUEL_TO_CAR_TABLE[String(fuel || '').toLowerCase()]
  if (carFuel) parts.push(`fuel_type='${carFuel}'`)
  if (Number.isFinite(year)) {
    parts.push(`year>=${year - YEAR_WINDOW}`, `year<=${year + YEAR_WINDOW}`)
  }
  const km = mileageWindow(mileageKm)
  if (km) parts.push(`mileage_km>=${km[0]}`, `mileage_km<=${km[1]}`)
  const hp = powerBandBounds(hpBand)
  if (hp) {
    parts.push(`horsepower_hp>=${hp[0]}`, 'horsepower_hp>0')
    if (hp[1] !== null) parts.push(`horsepower_hp<${hp[1]}`)
  }
  return parts.join(' && ')
}

/**
 * Group the comparables by country. `nlValue` is the plate's own NL band median; each
 * country's landed median is expressed against it (negative = lands below NL value).
 */
export function summariseByCountry(rows, { nlValue = null } = {}) {
  const byCc = new Map()
  for (const r of rows || []) {
    const cc = (r.country_code || '').toUpperCase()
    if (!cc) continue
    if (!byCc.has(cc)) byCc.set(cc, [])
    byCc.get(cc).push(r)
  }
  const out = []
  for (const [cc, rs] of byCc) {
    if (rs.length < MIN_PER_COUNTRY) continue
    const asking = median(rs.map(r => num(r.price_amount_eur)))
    const costs = rs.map(landedCost).filter(v => v !== null)
    const landed = cc === 'NL' ? asking : costs.length >= MIN_PER_COUNTRY ? median(costs) : null
    out.push({
      cc,
      n: rs.length,
      medianAsking: asking,
      medianLanded: landed,
      landedN: cc === 'NL' ? rs.length : costs.length,
      medianKm: median(rs.map(r => num(r.mileage_km)).filter(v => v > 0)),
      medianYear: median(rs.map(r => num(r.year)).filter(v => v > 1950)),
      vsNlPct: nlValue > 0 && landed != null ? (landed - nlValue) / nlValue * 100 : null,
    })
  }
  // Home market first, then by depth of evidence.
  out.sort((a, b) => (a.cc === 'NL' ? -1 : b.cc === 'NL' ? 1 : b.n - a.n))
  return out
}
